import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DateField, FileField, FormErrorSummary, MoneyField, SelectField, TextField, applyServerErrors, zDateOnly, zMoney, zRequiredString } from '@/business';
import { Button, Drawer, DrawerContent, toast } from '@/design-system';
import { lookupsQuery } from '@/shared/lookups';
import { todayInDubai } from '@/shared/lib/dates';

import { useRecordPayment } from '../api/mutations';

const schema = z.object({ bankCode: zRequiredString(), receivedOn: zDateOnly(), amount: zMoney({ min: '0.01' }), reference: zRequiredString() });
type Values = z.infer<typeof schema>;

export function RecordPaymentDrawer({ open, onOpenChange, defaultBank, onRecorded }: { open: boolean; onOpenChange: (o: boolean) => void; defaultBank?: string | undefined; onRecorded: (p: Schema<'PaymentDetail'>) => void }) {
  const { t } = useTranslation('collections');
  const lookups = useQuery(lookupsQuery);
  const record = useRecordPayment();
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { bankCode: defaultBank ?? '', receivedOn: todayInDubai(), amount: '', reference: '' } });
  const submit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      const { payment: p, attached } = await record.mutateAsync({ body: { bankCode: v.bankCode, receivedOn: v.receivedOn, amount: v.amount, reference: v.reference.trim() }, file });
      toast.done(t('payments.record.recorded', { number: p.number }));
      if (!attached) toast.error(t('attachments.notAttached'));
      setFile(null);
      form.reset();
      onOpenChange(false);
      onRecorded(p);
    } catch (e) {
      const err = applyServerErrors(form, e);
      if (err) setError(err.messages.join(' ') || err.title);
    }
  });
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        title={t('payments.record.title')}
        description={t('payments.record.description')}
        footer={
          <>
            <Button onClick={() => onOpenChange(false)}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={record.isPending} onClick={() => void submit()}>
              {t('payments.record.submit')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4 px-5 py-4">
          {error ? <FormErrorSummary title={error} /> : null}
          <SelectField control={form.control} name="bankCode" label={t('payments.record.bank')} options={(lookups.data?.banks ?? []).map((b) => ({ value: b.code, label: b.name }))} />
          <div className="grid grid-cols-2 gap-4">
            <DateField control={form.control} name="receivedOn" label={t('payments.record.receivedOn')} max={todayInDubai()} />
            <MoneyField control={form.control} name="amount" label={t('payments.record.amount')} />
          </div>
          <TextField control={form.control} name="reference" label={t('payments.record.reference')} placeholder={t('payments.record.referencePlaceholder')} dir="ltr" />
          <button type="submit" hidden />
          <FileField label={t('payments.record.advice')} purpose="attachment" optional help={t('payments.record.adviceHelp')} onChange={setFile} />
        </form>
      </DrawerContent>
    </Drawer>
  );
}
